import { Location } from "@angular/common";
import { Injectable, OnDestroy, inject } from "@angular/core";
import {
  NavigationBehaviorOptions,
  NavigationCancel,
  NavigationEnd,
  NavigationExtras,
  NavigationStart,
  Router,
} from "@angular/router";
import { BaseService } from "@providers/BaseService";
import { C_ItemsMenu, navigationModel } from "@sports-models/index";
import { SportService, SportsCommonService } from "@sports-services/index";
import { SportsbookFacade } from "@sports-stores/sportsbook";
import { BehaviorSubject, Subject, takeUntil } from "rxjs";
import { SPORTS_NAVIGATION_CONFIG } from "./sports.navigation.config";
import { checkChangesInParams, checkParamsAndQueryParams, isSportsPage } from "./sports.navigation.utils";

@Injectable({ providedIn: "root" })
export class SportNavigationService extends BaseService implements OnDestroy {
  itemsMenu: C_ItemsMenu[];
  itemsMenu$: BehaviorSubject<C_ItemsMenu[]> = new BehaviorSubject([]);
  router = inject(Router);
  sportbookFacade = inject(SportsbookFacade);
  location = inject(Location);
  sportService = inject(SportService);
  SportsCommonService = inject(SportsCommonService);

  private ngUnsubscribe$ = new Subject<boolean>();

  navigationState$: BehaviorSubject<navigationModel> = new BehaviorSubject({
    currentUrl: {
      url: null,
      state: null,
    },
    prevUrl: null,
    history: [],
  });
  redirectTo = false;
  stateFromLocationOnNavigationStarts: any;
  popstate = false;
  useQueryParams = false;
  lastNodeRestored;

  constructor() {
    super();
    this.router.events.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((val) => {
      const SplitPage = val["url"]?.split("?");
      const filteredQueryParams = {};
      const queryParams = {};
      if (SplitPage?.[1]) {
        const targetPage = SplitPage[0].slice(1);
        const shouldCheckParams = SPORTS_NAVIGATION_CONFIG[targetPage]?.PARAMS?.length;
        new URLSearchParams(SplitPage[1]).forEach((value, key) => {
          queryParams[key] = value;
          if (shouldCheckParams && SPORTS_NAVIGATION_CONFIG[targetPage]?.PARAMS.includes(key)) {
            filteredQueryParams[key] = value;
          }
        });
      }
      const stateFromLocation = this.location.getState() || {};
      const stateFromRouter = this.router.getCurrentNavigation().extras?.state;
      if (val instanceof NavigationStart) {
        if (val.navigationTrigger === "popstate") {
          this.popstate = true;
        }
        // identify if it is a back, forward action from browser button, .back() .historyGo() etc action action from location, etc
        const page = this.location.path().split("?")[0];
        if (val.url.includes(page) && !stateFromRouter) {
          this.stateFromLocationOnNavigationStarts = stateFromLocation;
        }

        const stateToCheck = !this.stateFromLocationOnNavigationStarts
          ? stateFromRouter
          : this.stateFromLocationOnNavigationStarts;
        const checkResult = checkParamsAndQueryParams(stateToCheck || {}, val.url, filteredQueryParams);
        this.redirectTo = checkResult.shouldRedirect;
        this.useQueryParams = checkResult.shouldUseQueryParams;
      }

      if (val instanceof NavigationEnd) {
        let state = !this.stateFromLocationOnNavigationStarts
          ? stateFromRouter
          : this.stateFromLocationOnNavigationStarts;
        state = {
          ...state,
          queryParams,
        };
        if (this.useQueryParams) {
          state = {
            ...state,
            params: filteredQueryParams,
          };
        }
        const navigationStateValue = this.navigationState$.getValue();
        const currentUrlIsDiferentOfNewValue = navigationStateValue.currentUrl.url !== val.urlAfterRedirects;
        const currentUrlEqualToNewValueWithDiferentParams =
          navigationStateValue.currentUrl.url === val.urlAfterRedirects &&
          checkChangesInParams(navigationStateValue.currentUrl.state.params, state.params);
        if (
          (currentUrlIsDiferentOfNewValue || currentUrlEqualToNewValueWithDiferentParams) &&
          val.urlAfterRedirects &&
          !this.popstate
        ) {
          state = this.injectSportsNavigationIdToLocation(state);
          const nextCurrent = {
            url: val.urlAfterRedirects ? val.urlAfterRedirects : val.url,
            state: state,
            redirectFrom: val.urlAfterRedirects && val.urlAfterRedirects !== val.url ? val.url : null,
          };
          const nextHistory = navigationStateValue.currentUrl.url
            ? [navigationStateValue.currentUrl, ...navigationStateValue.history]
            : navigationStateValue.history;
          this.sportbookFacade.navigationUpdate({
            prevUrl: navigationStateValue.currentUrl,
            currentUrl: nextCurrent,
            history: nextHistory,
          });
          this.navigationState$.next({
            prevUrl: navigationStateValue.currentUrl,
            currentUrl: nextCurrent,
            history: nextHistory,
          });
        }
        if (this.popstate && val.url !== "/") {
          state = this.injectSportsNavigationIdToLocation(state, stateFromRouter?.sportsNavigationId);
          const newCurrentUrl = {
            ...navigationStateValue.currentUrl,
            state: {
              ...navigationStateValue.currentUrl.state,
              sportsNavigationId: state.sportsNavigationId,
            },
            popstate: true,
          };
          this.sportbookFacade.historyUpdate({
            prevUrl: navigationStateValue.prevUrl,
            currentUrl: newCurrentUrl,
            history: navigationStateValue.history,
          });
          this.navigationState$.next({
            prevUrl: navigationStateValue.prevUrl,
            currentUrl: newCurrentUrl,
            history: navigationStateValue.history,
          });
        }
        this.stateFromLocationOnNavigationStarts = undefined;
        this.popstate = false;
      }

      if (val instanceof NavigationCancel) {
        this.stateFromLocationOnNavigationStarts = undefined;
        this.popstate = false;
      }
    });
  }

  ngOnDestroy() {
    this.ngUnsubscribe$.next(true);
    this.ngUnsubscribe$.complete();
  }

  redirectionDone() {
    this.redirectTo = false;
  }

  back() {
    this.location.back();
  }

  navigateByUrl(url: string, option?: NavigationBehaviorOptions) {
    this.router.navigateByUrl(url, option);
  }

  navigate(commands: any[], extras?: NavigationExtras) {
    this.router.navigate(commands, extras);
  }

  tryToGoToEvent(selection) {
    this.sportService
      .getGameByNodeId(selection.ResultsNr)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((data) => {
        if (!data) {
          this.SportsCommonService.showDismiss("", "No hay mercados abiertos en este evento.");
          return;
        }
        this.sportService.deepLinkToEvent(data);
      });
  }

  injectLastStateToHistory(sportsNavigationId, stateToBeRestored) {
    let newHistory = this.navigationState$.getValue().history;
    if (newHistory.length > 1) {
      const newItem = {
        ...this.navigationState$.getValue().history[0],
        stateToBeRestored,
      };
      newHistory = [newItem, ...this.navigationState$.getValue().history.slice(1)];
    }
    const nextNavigationState = {
      ...this.navigationState$.getValue(),
      history: newHistory,
    };
    this.navigationState$.next({
      ...nextNavigationState,
      updated: true,
    });

    this.sportbookFacade.historyUpdate({
      ...nextNavigationState,
    });
  }

  getRestoreStateFromHistory(sportsNavigationId) {
    if (!sportsNavigationId) {
      return undefined;
    }
    const navigationState = this.navigationState$.getValue();
    const currentHistory = [...navigationState.history];
    let stateToBeRestored;
    const targetIndex = currentHistory.findIndex(
      (historyNode) => historyNode.state?.sportsNavigationId === sportsNavigationId,
    );
    if (targetIndex !== -1) {
      this.lastNodeRestored = currentHistory[targetIndex];
      stateToBeRestored = currentHistory[targetIndex]?.stateToBeRestored;
      currentHistory.splice(targetIndex, 1);
      const nextNavigationState = {
        ...navigationState,
        history: currentHistory,
      };
      this.navigationState$.next({ ...nextNavigationState, updated: true });

      this.sportbookFacade.historyUpdate({
        ...nextNavigationState,
      });
    } else {
      this.lastNodeRestored = undefined;
    }
    return stateToBeRestored;
  }

  isLastNodeRestored(sportsNavigationId) {
    if (!sportsNavigationId) {
      return false;
    }
    return sportsNavigationId === this.lastNodeRestored?.state.sportsNavigationId;
  }

  injectSportsNavigationIdToLocation(state, sportsNavigationId?) {
    const path = this.location.path();
    const page = path.split("?")[0];
    let newState = { ...state };
    const usedSportsNavigationId = sportsNavigationId ? sportsNavigationId : Date.now();
    if (isSportsPage(page)) {
      newState = { ...state, sportsNavigationId: usedSportsNavigationId };
      this.location.replaceState(path, undefined, {
        ...state,
        sportsNavigationId: usedSportsNavigationId,
      });
    }
    return newState;
  }

  // resetHistory() {
  //   const current = this.navigationState$.getValue();
  //   const nextNavigationState = {
  //     ...current,
  //     history: [current.history[current.history.length - 1]],
  //   };
  //   this.navigationState$.next({ ...nextNavigationState, updated: true });
  //   this.sportbookFacade.historyUpdate({
  //     ...nextNavigationState,
  //   });
  // }
}
